import cn from 'classnames';
import { useState, useRef } from 'preact/hooks';
import style from './QuantitySelector.module.scss';
import useRoute from '@/widgets/hooks/useRoute';
import useTranslations from '@/widgets/hooks/useTranslations';
import type { Translations } from '@/widgets/ProductList/config';
import { calcQuantitySelectorAmounts } from './utils.ts';

export type QuantitySelectorProps = {
  quantity: number;
  onOptimisticUpdate: (v: number) => void;
  onClose: () => void;
  step: number;
  maxOrderQuantity: number;
  quantities: number[];
};

const NUMBER_REGEX = /[0-9]/;

export default function QuantitySelector({
  quantity,
  onOptimisticUpdate,
  onClose,
  maxOrderQuantity,
  step,
  quantities
}: QuantitySelectorProps) {
  const route = useRoute();
  const { t } = useTranslations<Translations>();
  const [modifiedQuantity, setModifiedQuantity] = useState<number>();
  const qty = modifiedQuantity ?? quantity;
  const quantitiesSet = new Set(quantities);
  const inputRef = useRef<HTMLInputElement>(null);

  // Note: the form is submitted when no-errors (pre-validated)
  function onSubmit(e: SubmitEvent) {
    e.preventDefault();
    e.stopPropagation();

    const form = e.target as HTMLFormElement;
    const amount = +new FormData(form).get('quantity')!;
    onOptimisticUpdate(amount);
    onClose();
  }

  function validateQuantity(newQuantity: number) {
    const input = inputRef.current!;
    const isValid =
      validateMultiple(newQuantity) && validateIncorrectQuantity(newQuantity);

    if (isValid) {
      input.setCustomValidity('');
      setModifiedQuantity(newQuantity);
    }

    input.reportValidity();

    return isValid;
  }

  function validateMultiple(newQuantity: number) {
    if (newQuantity % step === 0) return true;
    inputRef.current!.setCustomValidity(
      t('common.product.a2c.submit.validation.multiple', { step })
    );
    return false;
  }

  function validateIncorrectQuantity(newQuantity: number) {
    if (quantitiesSet.has(newQuantity)) return true;

    const formatter = new Intl.ListFormat(route.locale, {
      style: 'long',
      type: 'conjunction'
    });

    inputRef.current!.setCustomValidity(
      t('common.product.a2c.submit.validation.NotAvailable', {
        quantity: newQuantity,
        quantities: formatter.format(quantities.map((q) => q.toString()))
      })
    );

    return false;
  }

  return (
    <form method="dialog" onSubmit={onSubmit} class={style.popover}>
      <ul>
        {calcQuantitySelectorAmounts(step).map((amount) => (
          <li key={amount}>
            <button
              type="submit"
              disabled={!quantitiesSet.has(amount)}
              class={cn({
                [style.active]: amount === qty
              })}
              onKeyDown={(e) => {
                if (hasBeenPressed(e) && validateQuantity(amount)) {
                  onOptimisticUpdate(amount);
                }
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
                if (validateQuantity(amount)) {
                  onOptimisticUpdate(amount);
                  // Required manual onClose because:
                  // 1. stopPropagation() prevents the normal form submission flow
                  // 2. method="dialog" would close without state updates
                  // 3. Ensures immediate UI consistency before modal dismissal
                  onClose();
                }
              }}
            >
              {amount}
            </button>
          </li>
        ))}
      </ul>
      <fieldset>
        <input
          type="number"
          min={0}
          // Note: Usually it's set to zero meaning no limit (better undefined for us)
          max={maxOrderQuantity || undefined}
          ref={inputRef}
          step={step}
          onKeyPress={(e) => {
            if (!hasBeenPressed(e) && !NUMBER_REGEX.test(e.key)) {
              e.preventDefault();
            }
          }}
          onInput={(e) =>
            validateQuantity((e.target as HTMLInputElement).valueAsNumber)
          }
          name="quantity"
          value={qty}
          placeholder={t('common.product.a2c.input.placeholder')}
        />
        <button type="submit">
          {t('common.product.a2c.submit.buttonText')}
        </button>
      </fieldset>
    </form>
  );
}

function hasBeenPressed(e: KeyboardEvent) {
  return e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar';
}
